import * as React from 'react';
import { cn } from '../../lib/utils';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import type { Domain } from '../../state/domain-state';
import { ResolvedUserName } from '../users/ResolvedUserName';

/**
 * Props for the DomainCard component.
 */
export interface DomainCardProps {
  readonly domain: Domain;
  readonly onClick: (domainId: string) => void;
}

/**
 * DomainCard renders a clickable card displaying domain summary information:
 * domain name, truncated zone ID, credential status indicator, created date, and created-by user.
 *
 * The entire card is clickable and keyboard-navigable (Enter activates navigation).
 * Meets WCAG 2.1 AA focus-visible requirements and minimum 44x44px touch target.
 *
 * Validates: Requirements 1.1, 1.2, 12.2, 12.3, 11.3
 */
export function DomainCard({ domain, onClick }: DomainCardProps): React.JSX.Element {
  const handleClick = (): void => {
    onClick(domain.domainId);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onClick(domain.domainId);
    }
  };

  const truncatedZoneId = domain.cloudflareZoneId.length > 8
    ? `${domain.cloudflareZoneId.slice(0, 8)}...`
    : domain.cloudflareZoneId;

  const formattedDate = new Date(domain.createdAt).toLocaleDateString();

  const hasCredential = Boolean(domain.credentialId);

  return (
    <Card
      role="button"
      tabIndex={0}
      aria-label={`Domain ${domain.domainName}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={cn(
        'min-h-[44px] cursor-pointer transition-colors hover:bg-accent/50',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      )}
    >
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-lg">{domain.domainName}</CardTitle>
          <Badge variant={hasCredential ? 'success' : 'warning'}>
            {hasCredential ? 'Credential linked' : 'No credential'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <dl className="grid grid-cols-1 gap-1 text-sm">
          <div className="flex items-center gap-2">
            <dt className="text-muted-foreground">Zone ID:</dt>
            <dd className="font-mono text-xs">{truncatedZoneId}</dd>
          </div>
          <div className="flex items-center gap-2">
            <dt className="text-muted-foreground">Created:</dt>
            <dd>{formattedDate}</dd>
          </div>
          <div className="flex items-center gap-2">
            <dt className="text-muted-foreground">Created by:</dt>
            <dd><ResolvedUserName userId={domain.createdBy} /></dd>
          </div>
        </dl>
      </CardContent>
    </Card>
  );
}
